// Indexing a consultation's transcript + clinician notes into Ask Sophie's
// document store (tikcare_rag `POST /add_embedding`, `document_type=transcript`).
//
// Pure helpers only; the hook in MedicalScribeTranscriptionData.js decides
// WHEN to post (settle points + a 30 s idle timer) and this module decides
// WHAT goes on the wire. One document per consultation, keyed server-side by
// the consultation id: re-sending unchanged content is a no-op there, changed
// content supersedes the previous version, and a blank transcript + blank
// notes tells the backend to drop the document.

// Off unless the build's env file says otherwise (`.env.dev` only, for now).
// Same pattern as RAG_STREAM_ENABLED in constants/askSophieStreaming.js.
export const TRANSCRIPT_INDEX_ENABLED = process.env.REACT_APP_INDEX_TRANSCRIPTS === 'true';

// Typed edits are re-indexed this long after the last keystroke — long enough
// that an editing session costs one embedding pass, not one per pause.
export const TRANSCRIPT_IDLE_MS = 30000;

// TipTap's empty document; the report generator treats it as "no notes" too.
const EMPTY_NOTES_HTML = '<p></p>';

const asText = (value) => (value == null ? '' : String(value));

// Notes as the backend should see them: TipTap's empty document counts as blank.
export function normalizeNotes(notes) {
  const text = asText(notes);
  return text.trim() === EMPTY_NOTES_HTML ? '' : text;
}

// True when there is something worth indexing. The idle/flush path only posts
// when this holds, because a blank payload is a delete on the server.
export function hasTranscriptContent(transcript, notes) {
  return asText(transcript).trim() !== '' || normalizeNotes(notes).trim() !== '';
}

const pad2 = (n) => String(n).padStart(2, '0');

// "2026-09-20" in the browser's local zone — the zone the consultation title
// ("Consultation - Sep 20, 2026") is shown in, so the two agree. '' when the
// timestamp is missing or unparseable.
export function consultationDateYmd(createdAt) {
  if (!createdAt) return '';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// "Session transcript (Sep 20, 2026)": the same en-US short format the hook's
// convertToGMT8Date uses for consultation titles and report names.
export function transcriptFileName(createdAt) {
  const d = createdAt ? new Date(createdAt) : null;
  if (!d || Number.isNaN(d.getTime())) return 'Session transcript';
  const formatted = d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  return `Session transcript (${formatted})`;
}

// The multipart body for POST /add_embedding. `consultation` needs `id` and
// `createdAt`; `patient_id` and `consultation_id` are required by the backend.
export function buildTranscriptIndexForm({ patientId, teamId, consultation, transcript, notes }) {
  const form = new FormData();
  form.append('patient_id', asText(patientId));
  form.append('document_type', 'transcript');
  form.append('consultation_id', asText(consultation?.id));
  form.append('transcript', asText(transcript));
  form.append('notes', normalizeNotes(notes));
  const consultationDate = /^\d{4}-\d{2}-\d{2}$/.test(consultation?.session_date || '')
    ? consultation.session_date : consultationDateYmd(consultation?.createdAt);
  if (consultationDate) form.append('consultation_date', consultationDate);
  form.append('file_name', transcriptFileName(consultation?.createdAt));
  if (teamId != null) form.append('team_id', asText(teamId));
  return form;
}

// Client-side dedup key: the hook posts only when this differs from the last
// one it posted. A full-content key, so two different edits can never collide.
export function transcriptFingerprint(consultationId, transcript, notes) {
  return [asText(consultationId), asText(transcript), normalizeNotes(notes)].join('\u0000');
}
